import { useNavigate } from 'react-router-dom'

import { Avatar, Box, Icon, Spinner, Text } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import { Share } from '@factorialco/factorial-one-react/icons/app'
import { ReferralProgramPoliciesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { platformData } from 'modules/nps/lib/platformData'

interface ReviewButtonProps {
  onClose: () => void
}

export const ReviewButton = ({ onClose }: ReviewButtonProps) => {
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()
  const { data: policies, loading: policiesLoading } = useQuery(ReferralProgramPoliciesDocument)
  const canSeeReferralPage = policies?.apiCore._policies.seeReferralProgramPage.can
  const navigate = useNavigate()

  const handleReviewClick = (url: string, name?: string) => {
    if (canSeeReferralPage) {
      track(trackingEvents.nps.clickReferralProgram)
      navigate(url)
    } else {
      track(trackingEvents.nps.clickReview, { platform: name })
      window.open(url)
    }
    onClose()
  }

  const market =
    company.legalEntities.find((legalEntity) => legalEntity.main === true)?.country || ''
  const marketPlatformData =
    platformData.find((platform) => platform.mainMarket.includes(market)) ||
    platformData.find((platform) => platform.name == 'G2')

  if (!canSeeReferralPage && !marketPlatformData) {
    return null
  }

  if (policiesLoading) {
    return <Spinner />
  }

  const getConfig = () => {
    if (canSeeReferralPage) {
      return {
        title: i18n.t('nps.widget.referral_program.title'),
        description: i18n.t('nps.widget.referral_program.description'),
      }
    } else {
      return {
        title: i18n.t('nps.widget.review.title', { platform: marketPlatformData?.name || '' }),
        description: i18n.t('nps.widget.review.description'),
      }
    }
  }

  const Content = () => {
    if (canSeeReferralPage) {
      return (
        <Box maxWidth="fit-content">
          <Button
            icon={Share}
            label={i18n.t('nps.widget.referral_program.submit')}
            onClick={() => handleReviewClick(navigation.settings.referralProgram.fullPath())}
          />
        </Box>
      )
    } else {
      return (
        <Box
          maxWidth="s360"
          borderRadius={{ all: 'abs016' }}
          border={{
            all: {
              color: 'grey400',
              style: 'solid',
              width: 's1',
            },
          }}
          onClick={() =>
            handleReviewClick(marketPlatformData?.url || '', marketPlatformData?.name || '')
          }
          padding="s8"
          gap="s32"
          flexDirection="row"
          alignItems="center"
          justifyContent="spaceBetween"
        >
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Avatar
              label={marketPlatformData?.name || ''}
              url={marketPlatformData?.icon}
              size="s32"
            />
            <Text weight="bold" size="300">
              {i18n.t('nps.widget.review.submit', { platform: marketPlatformData?.name })}
            </Text>
          </Box>
          <Icon.Medium icon="external link" color="grey800" />
        </Box>
      )
    }
  }

  return (
    <>
      <Text size="300">
        <Text size="300" weight="bold">
          {getConfig().title}
        </Text>
        {getConfig().description}
      </Text>
      <Content />
    </>
  )
}
